import React, { useState, useMemo } from 'react';
import { MdEdit } from 'react-icons/md';
import { FaCaretUp, FaCaretDown } from 'react-icons/fa';
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Icon,
  IconButton,
  Spinner,
  Center,
  Box,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';

type OutrasPaginas = {
  seqPagina: number;
  nomPagina: string;
  datRegistro: any;
  dscUrl: string;
  codModeloPagina: number;
};

interface TabelaOutrasPaginasProps {
  listaOutrasPaginas: OutrasPaginas[];
  listaVazia: string;
  loading: boolean;
}

type Ordenacao = { chave: keyof OutrasPaginas; direcao: 'asc' | 'desc' } | null;

const TabelaOutrasPaginas = ({
  listaOutrasPaginas,
  listaVazia,
  loading,
}: TabelaOutrasPaginasProps) => {
  const navigate = useNavigate();

  const [ordenacao, setOrdenacao] = useState<Ordenacao>(null);
  const [colunaDestacada, setColunaDestacada] = useState<keyof OutrasPaginas | null>(null);

  const manipularOrdenacao = (chave: keyof OutrasPaginas) => {
    let direcao: 'asc' | 'desc' = 'asc';
    if (ordenacao && ordenacao.chave === chave && ordenacao.direcao === 'asc') {
      direcao = 'desc';
    }
    setOrdenacao({ chave, direcao });
  };

  const obterIndicadorOrdenacao = (chave: keyof OutrasPaginas) => {
    if (ordenacao && ordenacao.chave === chave) {
      return ordenacao.direcao === 'asc' ? (
        <FaCaretUp color="#1789FC" style={{ marginLeft: '4px' }} />
      ) : (
        <FaCaretDown color="#1789FC" style={{ marginLeft: '4px' }} />
      );
    }
      return <FaCaretUp color="#4a5568" style={{ marginLeft: '4px' }} />;
  };

  const listaOrdenada = useMemo(() => {
    const copia = [...listaOutrasPaginas];
    if (ordenacao !== null) {
      copia.sort((a, b) => {
        const valorA = a[ordenacao.chave];
        const valorB = b[ordenacao.chave];
        if (typeof valorA === 'string' && typeof valorB === 'string') {
          const comparacao = valorA.localeCompare(valorB);
          return ordenacao.direcao === 'asc' ? comparacao : -comparacao;
        }
        if (valorA < valorB) return ordenacao.direcao === 'asc' ? -1 : 1;
        if (valorA > valorB) return ordenacao.direcao === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return copia;
  }, [listaOutrasPaginas, ordenacao]);

  const diminuirTexto = (text: string | null, maxLength: number): string => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const renderRows = () =>
    listaOrdenada.length > 0 ? (
      listaOrdenada.map((pagina) => (
        <Tr key={pagina.seqPagina}>
          <Td>{pagina.nomPagina}</Td>
          <Td>{diminuirTexto(pagina.dscUrl, 30)}</Td>
          <Td>
            <IconButton
              aria-label="Botão Editar"
              bgColor="#fff"
              onClick={() => {
                if (pagina.codModeloPagina === 1) {
                  navigate(`editar-pagina-modelo-1/${pagina.seqPagina}`);
                } else if (pagina.codModeloPagina === 3) {
                  navigate(`editar-pagina-modelo-3/${pagina.seqPagina}`);
                } else if (pagina.codModeloPagina === 4) {
                  navigate(`editar-pagina-modelo-4/${pagina.seqPagina}`);
                } else if (pagina.codModeloPagina === 5) {
                  navigate(`editar-pagina-modelo-5/${pagina.seqPagina}`);
                } else {
                  navigate(`editar-pagina/${pagina.seqPagina}`);
                }
              }}
            >
              <Icon as={MdEdit} fontSize="24px" color="#1789FC" />
            </IconButton>
          </Td>
        </Tr>
      ))
    ) : (
      <Tr>
        <Td colSpan={5} color="#999898" textAlign="center">
          {listaVazia}
        </Td>
      </Tr>
    );

  return (
    <TableContainer>
      <Table variant="simple">
        <Thead>
          <Tr>
            <Th
              _hover={{ transition: '0.1s', color: '#1468C9' }}
              onClick={() => manipularOrdenacao('nomPagina')}
              onMouseEnter={() => setColunaDestacada('nomPagina')}
              onMouseLeave={() => setColunaDestacada(null)}
              cursor="pointer"
              color={ordenacao?.chave === 'nomPagina' ? 'blue.500' : undefined}
            >
              <Box display="flex" alignItems="center">
                Nome da Página {obterIndicadorOrdenacao('nomPagina')}
              </Box>
            </Th>
            <Th
              _hover={{ transition: '0.1s', color: '#1468C9' }}
              onClick={() => manipularOrdenacao('dscUrl')}
              onMouseEnter={() => setColunaDestacada('dscUrl')}
              onMouseLeave={() => setColunaDestacada(null)}
              cursor="pointer"
              color={ordenacao?.chave === 'dscUrl' ? 'blue.500' : undefined}
            >
              <Box display="flex" alignItems="center">
                URL {obterIndicadorOrdenacao('dscUrl')}
              </Box>
            </Th>
            <Th>Editar</Th>
          </Tr>
        </Thead>
        <Tbody>
          {loading ? (
            <Tr>
              <Td colSpan={5}>
                <Center>
                  <Spinner size="lg" color="blue.500" />
                </Center>
              </Td>
            </Tr>
          ) : (
            renderRows()
          )}
        </Tbody>
      </Table>
    </TableContainer>
  );
};

export default TabelaOutrasPaginas;
